import type { array } from "@amcharts/amcharts5";
import { BASE_METHOD, fetchApi, FetchApiOrigin } from "~/common/API/baseApi";

export default class OrderService {
  static getOrders = async (
    pageNumber: number = 1,
    advertiserId: number,
    status: string[] | null = null,
    publicationsStatus: string[] | null = null,
    isArchivedByAdvertiser: number | null = null
  ) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/orders",
      method: BASE_METHOD.GET,
      params: {
        pageNumber,
        "sortDirections[]": "desc",
        "sortFields[]": "id",
        advertiserId,
        status,
        publicationsStatus,
        isArchivedByAdvertiser,
      },
    });
    return response;
  };

  static getOrder = async (id: string, isFullScreenLoading = true) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: `/advert/order`,
      method: BASE_METHOD.GET,
      params: {
        id,
      },
      isFullScreenLoading,
    });
    return response;
  };

  static getOrderDailyStats = async (dateFrom: string, dateTo: string) => {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: `/advert/order-daily-stats`,
      method: BASE_METHOD.GET,
      params,
      isFullScreenLoading: false,
    });
    return response;
  };

  static createOrder = async (data: any) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/order",
      method: BASE_METHOD.POST,
      body: data,
      isFullScreenLoading: false,
    });
    return response;
  };

  // Создание заказа + креатива + публикаций (с per-channel креативами и schedule) одним запросом.
  static createOrderWithPublications = async (data: any) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/order/create-with-publications",
      method: BASE_METHOD.POST,
      body: data,
      isFullScreenLoading: false,
    });
    return response;
  };

  static updateOrder = async (data: any) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/order",
      method: BASE_METHOD.PUT,
      body: data,
      isFullScreenLoading: false,
    });
    return response;
  };

  static cancelOrder = async (id: string) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: `/advert/order/advertiser-cancel`,
      method: BASE_METHOD.PUT,
      params: {
        id,
      },
    });
    return response;
  };

  static cancelPayment = async (data: { transactionId: string; amount: number }) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: `/finance/acquiring/payment/cancel`,
      method: BASE_METHOD.POST,
      body: {
        transactionId: data.transactionId,
        amount: data.amount,
      },
    });
    return response;
  };

  static createPublicationSchedule = async (data: any) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/publication/schedule/create",
      method: BASE_METHOD.POST,
      body: data,
      isFullScreenLoading: false,
    });
    return response;
  };

  static updatePublicationSchedule = async (data: any) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/publication/schedule/row/update",
      method: BASE_METHOD.PUT,
      body: data,
      isFullScreenLoading: false,
    });
    return response;
  };

  static addCommentToPublicationSchedule = async (id: number, comment: string) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/publication",
      method: BASE_METHOD.PUT,
      body: { id, comment },
      isFullScreenLoading: false,
    });
    return response;
  };

  static getOrderAggregation = async (ids: Array<string>) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: `/advert/order/aggregation`,
      method: BASE_METHOD.GET,
      params: {
        ids,
      },
    });
    return response;
  };

  static confirmOrder = async (id: number) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/publication/advertiser-approve",
      method: BASE_METHOD.PUT,
      body: {
        id: id,
      },
    });
    return response;
  };

  static rejectOrder = async (id: number, reason: string) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/publication/advertiser-reject",
      method: BASE_METHOD.PUT,
      body: { id, reason },
    });
    return response;
  };

  static getOrdersCount = async () => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/orders/count",
      method: BASE_METHOD.GET,
      params: {
        advertiserId: localStorage.getItem("useUserStore__id"),
      },
    });
    return response;
  };

  static getOrdersTabs = async () => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/orders/tab",
      method: BASE_METHOD.GET,
      params: {
        advertiserId: localStorage.getItem("useUserStore__id"),
      },
    });
    return response;
  };

  static getOrdersDailyStats = async (id: string) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/order-daily-stats",
      method: BASE_METHOD.GET,
      params: {
        id,
      },
    });
    return response;
  };

  static removePlaceFromOrder = async (id: number) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/publication",
      method: BASE_METHOD.DELETE,
      body: { id },
    });
    return response;
  };

  static getOrdKKTU = async () => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/creative/kktu",
      method: BASE_METHOD.GET,
    });
    return response;
  };

  static deleteOrder = async (id: string) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/order",
      method: BASE_METHOD.DELETE,
      body: { id },
    });
    return response;
  };

  static archiveOrder = async (id: string) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/order/archive",
      method: BASE_METHOD.POST,
      body: { id },
    });
    return response;
  };

  static unarchiveOrder = async (id: string) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/advert/order/unarchive",
      method: BASE_METHOD.POST,
      body: { id },
    });
    return response;
  };
}
